/**
 * Attachment classification, shared by the thread and the composer.
 *
 * Both surfaces import this so a file looks the same queued as it does sent —
 * previously the composer showed a generic chip for everything while the
 * bubble rendered photos inline, so an image changed appearance the instant
 * you pressed Send.
 *
 * Classification is mime-first, extension-second: Roam-relayed attachments
 * always carry a mime type, but files mirrored from Roam occasionally arrive
 * as application/octet-stream, and then the filename is all we have.
 */

const extensionOf = (name = "") => {
  const match = /\.([a-z0-9]{1,8})$/i.exec(String(name).trim());

  return match ? match[1].toLowerCase() : "";
};

const DOCUMENTS = ["doc", "docx", "xls", "xlsx", "csv", "ppt", "pptx", "txt", "rtf", "pages", "numbers", "key"];
const ARCHIVES = ["zip", "rar", "7z", "gz", "tar", "bz2"];

/**
 * One of: image · video · audio · pdf · document · archive · file.
 * PDFs are their own kind rather than a generic document because they are by
 * far the most common thing merchants send (invoices, warranty cards,
 * service reports) and deserve a recognisable card.
 */
export const kindOf = (attachment = {}) => {
  const mime = String(attachment.mime || "").toLowerCase();
  const ext = extensionOf(attachment.name);

  if (attachment.type === "photo" || mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/") || attachment.type === "voice") return "audio";
  if (mime === "application/pdf" || ext === "pdf") return "pdf";
  if (DOCUMENTS.includes(ext)) return "document";
  if (ARCHIVES.includes(ext)) return "archive";

  return "file";
};

/** Short uppercase badge for a file card — "PDF", "XLSX", "ZIP". */
export const extensionLabel = (attachment = {}) => {
  const ext = extensionOf(attachment.name);

  if (ext) return ext.toUpperCase().slice(0, 4);

  // No usable filename: fall back to the mime subtype, which at least
  // distinguishes octet-stream from something meaningful.
  const subtype = String(attachment.mime || "").split("/")[1] || "";

  return subtype ? subtype.toUpperCase().slice(0, 4) : "FILE";
};

const KIND_ICONS = {
  image: "Image",
  video: "Camera",
  audio: "Activity",
  pdf: "FileText",
  document: "FileText",
  archive: "Package",
  file: "Paperclip",
};

export const iconForKind = (kind) => KIND_ICONS[kind] || "Paperclip";

/** True for kinds we render as media rather than as a file card. */
export const isMedia = (attachment) => {
  const kind = kindOf(attachment);

  return kind === "image" || kind === "video" || kind === "audio";
};
